import React, { useCallback, useState } from "react";

import { TFunction } from "i18next";
import {
    InterfaceProductWithTechMap,
    ProductListInterfaceForWriteOffPage,
    ProductToWriteOffCustomInterface,
} from "@me-team/common/src/interfaces/interfaces";

type GraphQLValidationMap = {
    [productId: string]: {
        code: string;
        message: string;
    }[];
};

export const useGraphQLWriteOffValidationHandler = (
    setCombinedProducts: React.Dispatch<
        React.SetStateAction<{
            productFromTechMap: InterfaceProductWithTechMap[] | null;
            addedProductToWriteOff: ProductListInterfaceForWriteOffPage[] | null;
        }>
    >,
    translate: TFunction<"translation">
) => {
    const [isWriteOffCompletedModalOpen, setIsWriteOffCompletedModalOpen] = useState(false);

    const closeModal = () => setIsWriteOffCompletedModalOpen(false);

    const applyGraphQLValidationErrors = useCallback(
        (gqlValidation: GraphQLValidationMap) => {
            const bookingErrors = gqlValidation["booking"];
            const shouldOpenModal = bookingErrors?.some((err) => err.code === "write_off.write_off_was_completed");

            if (shouldOpenModal) {
                setIsWriteOffCompletedModalOpen(true);
            }

            setCombinedProducts((prevState) => {
                const updateProduct = (product: any) => {
                    const productId = String(product.product?.id ?? product.id);
                    const productErrors = gqlValidation[productId];
                    if (!productErrors) return product;

                    let updatedErrors = { ...product.errors };

                    for (const err of productErrors) {
                        if (err.code === "write_off.not_enough_amount") {
                            updatedErrors.qtyError = translate("Exceeded balance.");
                        }
                    }

                    return {
                        ...product,
                        hasError: true,
                        errors: updatedErrors,
                    };
                };

                const updatedProductFromTechMap =
                    prevState.productFromTechMap?.map((techMap) => ({
                        ...techMap,
                        techMapProductInfo: techMap.techMapProductInfo?.map(updateProduct) ?? [],
                    })) ?? [];

                const updatedAddedProductToWriteOff = prevState.addedProductToWriteOff?.map(updateProduct) ?? [];

                return {
                    productFromTechMap: updatedProductFromTechMap,
                    addedProductToWriteOff: updatedAddedProductToWriteOff,
                };
            });
        },
        [setCombinedProducts, translate]
    );

    const applyNotEnoughQuantityCategoryError = useCallback(
        ({
            productId,
            storageId,
            writeOffProductsList,
        }: {
            productId: number;
            storageId: number;
            writeOffProductsList: ProductToWriteOffCustomInterface[];
        }): boolean => {
            const targetItem = writeOffProductsList.find(
                (item) => item.product === productId && item.storage === storageId
            );

            if (!targetItem) return false;

            const targetTechMapId = targetItem.techMapId ?? null;

            const applyQtyError = <
                T extends { hasError: boolean; errors: { storageError: string | null; qtyError: string | null } },
            >(
                product: T
            ): T => ({
                ...product,
                hasError: true,
                errors: {
                    ...product.errors,
                    qtyError: translate("Exceeded balance."),
                },
            });

            setCombinedProducts((prevState) => {
                const updatedProductFromTechMap =
                    prevState.productFromTechMap?.map((techMap) => {
                        if (targetTechMapId === null || techMap.techMapId !== targetTechMapId) return techMap;

                        return {
                            ...techMap,
                            techMapProductInfo:
                                techMap.techMapProductInfo?.map((product) =>
                                    product.product?.id === productId ? applyQtyError(product) : product
                                ) ?? [],
                        };
                    }) ?? [];

                const updatedAddedProductToWriteOff =
                    prevState.addedProductToWriteOff?.map((product) =>
                        targetTechMapId === null && product.id === productId ? applyQtyError(product) : product
                    ) ?? [];

                return {
                    productFromTechMap: updatedProductFromTechMap,
                    addedProductToWriteOff: updatedAddedProductToWriteOff,
                };
            });

            return true;
        },
        [setCombinedProducts, translate]
    );

    return {
        applyGraphQLValidationErrors,
        applyNotEnoughQuantityCategoryError,
        isWriteOffCompletedModalOpen,
        closeWriteOffCompletedModal: closeModal,
    };
};
